import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { createLogger } from "debug/DebugingLogger";
import type { SslMode } from "./postgresApi";

const log = createLogger("savedConnectionsApi");

export type ConnectionType =
  "postgres" | "supabase" | "google_oauth" | "bucket" | "local_sqlite";

/** Wire values match the Rust `BucketProviderKind` enum in `common_core`. */
export type BucketProviderKind = "s3" | "gcs" | "azure";

/** Mirrors `ConnectionScope` in `common_core`. */
export type BucketScope =
  { kind: "bucket"; name: string } | { kind: "account" };

/** Non-secret view. Server never returns passwords / OAuth tokens / CA cert bodies. */
export interface SavedConnection {
  id: string;
  display_name: string;
  /** Unix seconds. */
  created_at: number;
  /** Unix seconds. */
  updated_at: number;
  /** Unix seconds; null when the connection has never fulfilled a sync. */
  last_used_at: number | null;
  credentials: SavedConnectionCredentials;
}

export type SavedConnectionCredentials =
  | PostgresLikeCredentials
  | GoogleOauthCredentials
  | BucketCredentials
  | LocalSqliteCredentials;

export interface PostgresLikeCredentials {
  type: "postgres" | "supabase";
  host: string;
  port: number;
  database: string;
  username: string;
  ssl_mode: SslMode;
  has_ca_cert_pem: boolean;
}

export interface GoogleOauthCredentials {
  type: "google_oauth";
  account_email: string;
  scopes: string[];
  /** Unix seconds. The FE uses this only to surface "expires in N hours"
   *  in the read-only modal — refresh is server-side via the
   *  /sheets-access-token endpoint and is invisible to clients. */
  access_token_expires_at: number;
}

/** Non-secret view — the encrypted provider blob is never returned. The auth
 *  summary lets the connection-edit overlay render method / audience / linked
 *  account straight from the list (no extra round-trip); the email is resolved
 *  client-side from the linked Google connection in the same list. */
export interface BucketCredentials {
  type: "bucket";
  provider: BucketProviderKind;
  scope: BucketScope;
  /** `null` on legacy rows created before the auth summary was stored. */
  auth_method?: "user_oauth_direct" | "user_oauth_federation" | null;
  /** WIF audience (federation only). */
  wif_audience?: string | null;
  linked_google_saved_connection_id?: string | null;
}

/** Non-secret view of a local SQLite connection. The actual file lives in
 *  the user's browser-attached FSA handle (per device). `sha256_hash` is
 *  used to verify a re-attach matches the canonical file the connection
 *  was first created with. */
export interface LocalSqliteCredentials {
  type: "local_sqlite";
  sha256_hash: string;
  file_size: number;
}

export const isPostgresLike = (
  c: SavedConnectionCredentials,
): c is PostgresLikeCredentials =>
  c.type === "postgres" || c.type === "supabase";

export const isGoogle = (
  c: SavedConnectionCredentials,
): c is GoogleOauthCredentials => c.type === "google_oauth";

export const isBucket = (
  c: SavedConnectionCredentials,
): c is BucketCredentials => c.type === "bucket";

export const isLocalSqlite = (
  c: SavedConnectionCredentials,
): c is LocalSqliteCredentials => c.type === "local_sqlite";

const SHEETS_SCOPE_URLS = new Set([
  "https://www.googleapis.com/auth/drive.file",
  "https://www.googleapis.com/auth/drive.readonly",
  "https://www.googleapis.com/auth/drive",
  "https://www.googleapis.com/auth/spreadsheets",
  "https://www.googleapis.com/auth/spreadsheets.readonly",
]);

/** True when the Google credential carries a scope that unlocks the
 *  Sheets / Drive Picker. A credential minted via the bucket-connector
 *  flow has only `openid` / `email` / `profile` and returns false here
 *  — invoking the Picker with such a token yields Google's 403 page. */
export const hasGoogleSheetsScope = (c: GoogleOauthCredentials): boolean =>
  c.scopes.some((s) => SHEETS_SCOPE_URLS.has(s));

export interface CreateSavedConnectionRequest {
  display_name: string;
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  ssl_mode: SslMode;
  ca_cert_pem?: string;
  /** Postgres or Supabase only — Google connections come from the OAuth
   *  callback, not this endpoint. Sending `google_oauth` returns 400. */
  connection_type?: "postgres" | "supabase";
  /** D24: org to mint into. Omitted = the caller's personal org. */
  org_id?: string;
}

/** Use `clear_ca_cert: true` to drop the cert — null is ambiguous serverside. */
export interface UpdateSavedConnectionRequest {
  display_name?: string;
  password?: string;
  ssl_mode?: SslMode;
  ca_cert_pem?: string;
  clear_ca_cert?: boolean;
}

export interface StartGoogleOauthResponse {
  auth_url: string;
}

const BASE = "/api/saved-connections";

/** How to narrow the listing (D24). `modelId` is the canvas picker's spelling:
 *  the server resolves that model's org, so the picker asks the bind door's
 *  exact question and can never offer a source the door will refuse. `orgId` is
 *  the settings page's, from the active org. Neither = today's whole list. */
export interface SavedConnectionScope {
  orgId?: string | null;
  modelId?: string | null;
}

export const listSavedConnections = async (
  scope: SavedConnectionScope = {},
): Promise<SavedConnection[]> => {
  try {
    const params: Record<string, string> = {};
    if (scope.modelId) params.model_id = scope.modelId;
    else if (scope.orgId) params.org_id = scope.orgId;
    const res = await axiosInstance.get<{ connections: SavedConnection[] }>(
      BASE,
      { params },
    );
    return res.data.connections;
  } catch (error) {
    log.error("listSavedConnections failed:%o", error);
    throw mapError(error, "Failed to load saved connections");
  }
};

export const createSavedConnection = async (
  body: CreateSavedConnectionRequest,
): Promise<SavedConnection> => {
  try {
    const res = await axiosInstance.post<SavedConnection>(BASE, body);
    return res.data;
  } catch (error) {
    log.error("createSavedConnection failed:%o", error);
    throw mapError(error, "Failed to create saved connection");
  }
};

/** Body shape for `POST /api/saved-connections/local-sqlite` (T3).
 *  Server validates `sha256_hash` is a 64-char lowercase hex string
 *  and enforces the shared per-user cap; everything else is a
 *  plain create. */
export interface CreateLocalSqliteSavedConnectionRequest {
  display_name: string;
  sha256_hash: string;
  file_size: number;
  /** D24: org to mint into. Omitted = the caller's personal org. */
  org_id?: string;
}

/** Thrown by [[createLocalSqliteSavedConnection]] when the server detects the
 *  user already has a connection with the same `sha256_hash` (server returns
 *  409 with `existing_id` + `existing_display_name`). Catchers can offer the
 *  user a "Open existing connection" affordance instead of creating a
 *  duplicate row. */
export class DuplicateLocalSqliteError extends Error {
  readonly existingConnectionId: string;
  readonly existingDisplayName: string;
  constructor(
    existingConnectionId: string,
    existingDisplayName: string,
    message: string,
  ) {
    super(message);
    this.name = "DuplicateLocalSqliteError";
    this.existingConnectionId = existingConnectionId;
    this.existingDisplayName = existingDisplayName;
  }
}

export const createLocalSqliteSavedConnection = async (
  body: CreateLocalSqliteSavedConnectionRequest,
): Promise<SavedConnection> => {
  try {
    const res = await axiosInstance.post<SavedConnection>(
      `${BASE}/local-sqlite`,
      body,
    );
    return res.data;
  } catch (error) {
    log.error("createLocalSqliteSavedConnection failed:%o", error);
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      const data = error.response.data as
        | {
            existing_id?: string;
            existing_display_name?: string;
            error?: string;
          }
        | undefined;
      if (data?.existing_id && data?.existing_display_name) {
        throw new DuplicateLocalSqliteError(
          data.existing_id,
          data.existing_display_name,
          data.error ?? "This SQLite file is already attached.",
        );
      }
    }
    throw mapError(error, "Failed to create local SQLite connection");
  }
};

export const updateSavedConnection = async (
  id: string,
  body: UpdateSavedConnectionRequest,
): Promise<SavedConnection> => {
  try {
    const res = await axiosInstance.patch<SavedConnection>(
      `${BASE}/${encodeURIComponent(id)}`,
      body,
    );
    return res.data;
  } catch (error) {
    log.error("updateSavedConnection failed:%o", error);
    throw mapError(error, "Failed to update saved connection");
  }
};

export const deleteSavedConnection = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`${BASE}/${encodeURIComponent(id)}`);
  } catch (error) {
    log.error("deleteSavedConnection failed:%o", error);
    throw mapError(error, "Failed to delete saved connection");
  }
};

/** `savedConnectionId` set only on the reauthorize path (server verifies
 *  the returned email matches the existing record). */
export const startGoogleOauth = async (params: {
  csrf: string;
  savedConnectionId?: string;
  /** "sheets" (default) → drive.file scope; "bucket" → openid + identity +
   *  devstorage.read_only, the unified set serving both bucket auth mechanisms
   *  (OAuth-direct and Workload Identity Federation). */
  clientType?: "sheets" | "bucket";
  /** D24: org for a NET-NEW source. Ignored on the reauthorize path, which
   *  inherits the existing row's org — re-deriving it there would silently
   *  relocate the source and break every table bound to it. */
  orgId?: string;
}): Promise<StartGoogleOauthResponse> => {
  try {
    const res = await axiosInstance.post<StartGoogleOauthResponse>(
      `${BASE}/google/oauth-start`,
      {
        csrf: params.csrf,
        saved_connection_id: params.savedConnectionId,
        client_type: params.clientType,
        org_id: params.orgId,
      },
    );
    return res.data;
  } catch (error) {
    log.error("startGoogleOauth failed:%o", error);
    throw mapError(error, "Failed to start Google authorization");
  }
};

/** Thrown when the server answers `401 { error: "reauthorize_required" }` —
 *  Google rejected the stored refresh token, so the connection must be
 *  reauthorized before any Sheets call can succeed. Distinct type so callers
 *  can offer a reauthorize affordance instead of a dead-end error string. */
export class SheetsReauthorizeRequiredError extends Error {
  constructor(message?: string) {
    super(message ?? "This Google connection needs to be reauthorized.");
    this.name = "SheetsReauthorizeRequiredError";
  }
}

/** True when an axios error is the server's `401 reauthorize_required`. */
export const isReauthorizeRequired = (error: unknown): boolean =>
  axios.isAxiosError(error) &&
  error.response?.status === 401 &&
  (error.response?.data as { error?: string } | undefined)?.error ===
    "reauthorize_required";

/** Server status `reauthorize_required` ⇒ refresh token dead, prompt reauthorize. */
export const getSheetsAccessToken = async (
  savedConnectionId: string,
): Promise<string> => {
  try {
    const res = await axiosInstance.get<{ access_token: string }>(
      `${BASE}/${encodeURIComponent(savedConnectionId)}/sheets-access-token`,
    );
    return res.data.access_token;
  } catch (error) {
    log.error("getSheetsAccessToken failed:%o", error);
    if (isReauthorizeRequired(error)) {
      const msg = (error as { response?: { data?: { message?: string } } })
        .response?.data?.message;
      throw new SheetsReauthorizeRequiredError(msg);
    }
    throw mapError(error, "Failed to fetch Google access token");
  }
};

/** Wire shape of `GET /api/saved-connections/{id}/health`. `unreachable`
 *  is data about the external DB, not a transport failure — the server
 *  returns 200 for it. `checked_at` is unix seconds. */
export type ConnectionHealth =
  | { status: "reachable"; checked_at: number }
  | { status: "unreachable"; reason: string; checked_at: number }
  | { status: "not_applicable" };

export const getConnectionHealth = async (
  id: string,
): Promise<ConnectionHealth> => {
  try {
    const res = await axiosInstance.get<ConnectionHealth>(
      `${BASE}/${encodeURIComponent(id)}/health`,
    );
    return res.data;
  } catch (error) {
    log.error("getConnectionHealth failed:%o", error);
    throw mapError(error, "Failed to check connection health");
  }
};

function mapError(error: unknown, fallback: string): Error {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      { error?: string; message?: string } | undefined;
    const serverMsg =
      data?.message ?? data?.error ?? error.response?.statusText;
    if (serverMsg) return new Error(serverMsg);
  }
  return new Error(fallback);
}
